import { useEffect, useState, useCallback } from "react";
import superAdminApi from "../../../lib/superAdminApi";
import SuperAdminLayout from "../../../components/SuperAdminLayout";
import { ChevronLeft, ChevronRight, Pencil, X } from "lucide-react";
import { toast } from "react-toastify";

interface Subscription {
  id: string;
  billing_cycle?: string | null;
  is_active?: boolean | null;
  auto_renew?: boolean | null;
  start_date?: string | null;
  end_date?: string | null;
  Business?: { id: string; name?: string | null } | null;
  Package?: { id: string; name?: string | null } | null;
  FreeTrial?: { start_date?: string | null; end_date?: string | null }[];
}

interface EditForm {
  extend_days: string;
  billing_cycle: string;
  is_active: boolean;
}

const SuperAdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Subscription | null>(null);
  const [form, setForm] = useState<EditForm>({ extend_days: "", billing_cycle: "", is_active: true });
  const [saving, setSaving] = useState(false);

  const fetchSubs = useCallback(() => {
    setLoading(true);
    superAdminApi
      .get("/superadmin/subscriptions", { params: { page, limit: 20, status } })
      .then((res) => {
        setSubscriptions(res.data.subscriptions);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      })
      .catch((err) => toast.error(err.response?.data?.message ?? "Failed to load subscriptions"))
      .finally(() => setLoading(false));
  }, [page, status]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const openEdit = (sub: Subscription) => {
    setEditing(sub);
    setForm({
      extend_days: "",
      billing_cycle: sub.billing_cycle ?? "",
      is_active: sub.is_active ?? false,
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        is_active: form.is_active,
        billing_cycle: form.billing_cycle || undefined,
      };
      if (form.extend_days && Number(form.extend_days) > 0) {
        payload.extend_days = Number(form.extend_days);
      }
      await superAdminApi.put(`/superadmin/subscriptions/${editing.id}`, payload);
      toast.success("Subscription updated");
      setEditing(null);
      fetchSubs();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to update subscription");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SuperAdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
            <p className="text-gray-500 text-sm mt-1">{total.toLocaleString()} total</p>
          </div>
          <div className="flex gap-2">
            {(["all", "active", "expired"] as const).map((s) => (
              <button
                key={s}
                onClick={() => { setStatus(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                  status === s ? "bg-indigo-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-gray-500">
                  <th className="px-4 py-3 font-medium">Business</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Billing</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Start</th>
                  <th className="px-4 py-3 font-medium">End</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-32" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                      <td className="px-4 py-3"><div className="h-5 bg-gray-200 rounded-full w-14" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-4 py-3"><div className="h-7 bg-gray-200 rounded-lg w-7" /></td>
                    </tr>
                  ))
                ) : subscriptions.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No subscriptions found</td></tr>
                ) : (
                  subscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">{sub.Business?.name ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-600">{sub.Package?.name ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-600 capitalize">{sub.billing_cycle ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          sub.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                        }`}>
                          {sub.is_active ? "Active" : "Expired"}
                        </span>
                        {(sub.FreeTrial?.length ?? 0) > 0 && (
                          <span className="ml-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Trial</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {sub.start_date ? new Date(sub.start_date).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {sub.end_date ? new Date(sub.end_date).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openEdit(sub)}
                          title="Edit subscription"
                          className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h2 className="font-semibold text-gray-900">Edit Subscription</h2>
                <p className="text-xs text-gray-400 mt-0.5">{editing.Business?.name ?? "—"} · {editing.Package?.name ?? "—"}</p>
              </div>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* Current end date info */}
              <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                Current end date: <span className="font-medium text-gray-700">
                  {editing.end_date ? new Date(editing.end_date).toLocaleDateString() : "Not set"}
                </span>
              </div>

              {/* Extend by days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Extend by (days)</label>
                <input
                  type="number"
                  min="1"
                  value={form.extend_days}
                  onChange={(e) => setForm((f) => ({ ...f, extend_days: e.target.value }))}
                  placeholder="e.g. 30"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-400 mt-1">Adds days from current end date (or today if expired)</p>
              </div>

              {/* Billing cycle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
                <select
                  value={form.billing_cycle}
                  onChange={(e) => setForm((f) => ({ ...f, billing_cycle: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">— unchanged —</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              {/* Active status */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Active Status</label>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.is_active ? "bg-indigo-600" : "bg-gray-300"
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    form.is_active ? "translate-x-6" : "translate-x-1"
                  }`} />
                </button>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex gap-2 justify-end">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60 transition-colors"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminSubscriptions;
